import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { Button, IconButton, CircularProgress } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { faChevronRight, faPen, faTags } from "@fortawesome/pro-solid-svg-icons";

import type { ILabel, IPartner, IProfile } from "App/Modules/Models";
import { Permission } from "App/Modules/Models";
import { hasUserPermissionAnywhere } from "App/ReactComponents/Shared";

type PartnerDetailedInformationProps = {
    partner: IPartner;
    profile: IProfile;
    hasLimitedPermissions: boolean;
    labels: ILabel[];
    isLoading: boolean;
    isLabelsLoading: boolean;
    isLocaleAndTimeLoading: boolean;
    isDataPolicyLoading: boolean;
    chosenLabel: (label: ILabel) => void;
    isLocaleAndTimeDialogOpen: (value: boolean) => void;
    isEditLabelsDialogOpen: (value: boolean) => void;
    isDataPolicyDialogOpen: (value: boolean) => void;
};

export const PartnerDetailedInformation = (props: PartnerDetailedInformationProps) => {
    const {
        partner,
        profile,
        hasLimitedPermissions,
        labels,
        isLoading,
        isLabelsLoading,
        isLocaleAndTimeLoading,
        isDataPolicyLoading,
        chosenLabel,
        isLocaleAndTimeDialogOpen,
        isEditLabelsDialogOpen,
        isDataPolicyDialogOpen,
    } = props;
    const { t } = useTranslation();

    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

    const handleEditLocaleAndTime = () => {
        isLocaleAndTimeDialogOpen(true);
    };

    const handleEditLabels = (label) => {
        isEditLabelsDialogOpen(true);
        chosenLabel(label);
    };

    const handleEditDataPolicy = () => {
        isDataPolicyDialogOpen(true);
    };

    return isLoading ? (
        <React.Fragment>
            <Skeleton variant="rect" animation="wave" height="3em" width="100%" />
        </React.Fragment>
    ) : (
        <React.Fragment>
            {!hasLimitedPermissions ? (
                <div className="expandable-panel">
                    <Button
                        className="expandable-panel__button"
                        onClick={(e) => {
                            setIsDetailsExpanded(!isDetailsExpanded);
                            e.currentTarget.parentElement.classList.toggle("expandable-panel--expanded");
                        }}
                    >
                        <span className="button-text button-text--bold expandable-panel__button-text">
                            {!isDetailsExpanded
                                ? t("Entities.Partner.ShowDetailedInformation")
                                : t("Entities.Partner.HideDetailedInformation")}
                        </span>
                        <FontAwesomeIcon icon={faChevronRight} className="expandable-panel__button-icon" />
                    </Button>
                    <div className="expandable-panel__content">
                        <div className="card card--nested card--less-padding">
                            <div className="data-list data-list--extra-spacing">
                                <div className="data-list__header data-list__header--extra-spacing partner-details__header-with-edit">
                                    <h3 className="title title--h3 title--no-spacing">
                                        {t("Entities.Partner.LocaleAndTime")}
                                    </h3>
                                    {isLocaleAndTimeLoading ? (
                                        <span>
                                            <CircularProgress size={20} />
                                        </span>
                                    ) : null}
                                    <IconButton
                                        className="icon-button"
                                        onClick={handleEditLocaleAndTime}
                                        disabled={!hasUserPermissionAnywhere(profile, Permission.PartnerSettings)}
                                    >
                                        <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                                    </IconButton>
                                </div>
                                <div className="data-list__row">
                                    <p className="caption caption--no-spacing">
                                        {t("Entities.Partner.AutoLogoutMinutes")}
                                    </p>
                                    <div className="data-list__line" />
                                    <p className="paragraph paragraph--small paragraph--bold data-list__value">
                                        {t("Entities.Partner.AutoLogoutMinutesCount", {
                                            count: partner.autoLogoutMinutes,
                                            postProcess: "interval",
                                        })}
                                    </p>
                                </div>
                                <div className="data-list__row data-list__row--extra-spacing">
                                    <p className="caption caption--no-spacing">
                                        {t("Entities.TimeZone.DefaultTimeZone")}
                                    </p>
                                    <div className="data-list__line" />
                                    <div className="data-list__value">
                                        <p className="paragraph paragraph--small paragraph--bold paragraph--no-spacing">
                                            {t(`Entities.TimeZone.${partner.defaultTimeZoneName}`).split(")")[1]}
                                        </p>
                                        <p className="caption caption--italic caption--no-spacing data-list__value-subtext">
                                            {t(`Entities.TimeZone.${partner.defaultTimeZoneName}`).split(")")[0]})
                                        </p>
                                    </div>
                                </div>
                                <div className="data-list__row">
                                    <p className="caption caption--no-spacing">
                                        {t("Entities.Country.DefaultCountry")}
                                    </p>
                                    <div className="data-list__line" />
                                    <p className="paragraph paragraph--small paragraph--bold data-list__value">
                                        {t(`Entities.Country.${partner.defaultCountryName}`)}
                                    </p>
                                </div>
                                <div className="data-list__row">
                                    <p className="caption caption--no-spacing">
                                        {t("Entities.Language.DefaultLanguage")}
                                    </p>
                                    <div className="data-list__line" />
                                    <p className="paragraph paragraph--small paragraph--bold data-list__value">
                                        {t(`Entities.Language.${partner.defaultLanguageKey}`)}
                                    </p>
                                </div>
                                <div className="data-list__row">
                                    <p className="caption caption--no-spacing">{t("Entities.Locale.DefaultLocale")}</p>
                                    <div className="data-list__line" />
                                    <p className="paragraph paragraph--small paragraph--bold data-list__value">
                                        {t(`Entities.Locale.${partner.defaultLocale}`)}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="card card--nested card--less-padding">
                            <div className="data-list">
                                <div className="data-list__header partner-details__header-with-edit">
                                    <h3 className="title title--h3 title--no-spacing">
                                        {t("Entities.Partner.DataPolicy")}
                                    </h3>
                                    {isDataPolicyLoading ? (
                                        <span>
                                            <CircularProgress size={20} />
                                        </span>
                                    ) : null}
                                    <IconButton
                                        className="icon-button"
                                        disabled={hasLimitedPermissions}
                                        onClick={handleEditDataPolicy}
                                    >
                                        <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                                    </IconButton>
                                </div>
                                <div className="data-panels data-panels--rows">
                                    <div className="data-panels__panel data-panels__panel--extra-spacing">
                                        <div className="data-panels__heading">
                                            <p className="caption">{t("Entities.DataRetentions.AlarmVideos")}</p>
                                        </div>
                                        <div className="data-panels__heading">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {t("Entities.DataRetentions.DaysCount", {
                                                    count: 90,
                                                    postProcess: "interval",
                                                })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="data-panels__panel data-panels__panel--extra-spacing">
                                        <div className="data-panels__heading">
                                            <p className="caption">{t("Entities.DataRetentions.CameraImages")}</p>
                                        </div>
                                        <div className="data-panels__heading">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {t("Entities.DataRetentions.DaysCount", {
                                                    count: 90,
                                                    postProcess: "interval",
                                                })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="data-panels__panel data-panels__panel--extra-spacing">
                                        <div className="data-panels__heading">
                                            <p className="caption">{t("Entities.DataRetentions.InactiveIssues")}</p>
                                        </div>
                                        <div className="data-panels__heading">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {t("Entities.DataRetentions.DaysCount", {
                                                    count: 90,
                                                    postProcess: "interval",
                                                })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="data-panels__panel data-panels__panel--no-spacing">
                                        <div className="data-panels__heading">
                                            <p className="caption">{t("Entities.DataRetentions.RecordedVideo")}</p>
                                        </div>
                                        <div className="data-panels__heading">
                                            <p className="paragraph paragraph--small paragraph--bold">
                                                {partner.dataRetention.maxDaysRecordedVideo === null
                                                    ? t("Gui.Common.NoLimit")
                                                    : t("Entities.DataRetentions.DaysCount", {
                                                          count: partner.dataRetention.maxDaysRecordedVideo,
                                                          postProcess: "interval",
                                                      })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {!hasLimitedPermissions && labels ? (
                            <div className="card card--nested card--less-padding">
                                <h3 className="title title--h3 labels__header-title">
                                    <FontAwesomeIcon className="labels__header-icon" icon={faTags} />
                                    <span>{t("Entities.Label.Labels")}</span>
                                    <span className="caption labels__header-count">
                                        {`- ${labels.length} ${t("Entities.Value.Found")}`}
                                    </span>
                                    {isLabelsLoading ? (
                                        <span>
                                            <CircularProgress size={20} />
                                        </span>
                                    ) : null}
                                </h3>
                                <div className="entity-list entity-list--no-spacing">
                                    {labels.map((l) => {
                                        return (
                                            <div
                                                className="entity-list__item entity-list__item--narrow-stuffing"
                                                key={l.id}
                                            >
                                                <p className="paragraph paragraph--bold paragraph--no-spacing">
                                                    {l.label}
                                                </p>
                                                <IconButton
                                                    className="icon-button icon-button--small"
                                                    onClick={() => handleEditLabels(l)}
                                                    disabled={
                                                        !hasUserPermissionAnywhere(profile, Permission.PartnerSettings)
                                                    }
                                                >
                                                    <FontAwesomeIcon className="icon-button__icon" icon={faPen} />
                                                </IconButton>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </React.Fragment>
    );
};
